import { countAllocationDays, countWeekdays } from "./dayMath";

/** Rounds the way the planner always displayed hours: to two decimals. */
export const round2 = (value) => parseFloat(value.toFixed(2));

const EMPTY_HOURS = Object.freeze({ total: 0, byProject: {}, byScope: {}, byGroup: {}, byTask: {}, maxTaskInScope: {}, maxTaskInGroup: {} });

/**
 * Hours a resource is allocated within a closed range of days, in total and per project, scope, task group and
 * task, together with the most any single task of a scope or group has (which tells whether one task alone
 * exceeds a capacity). An allocation contributes its hours per day for each day of the range it covers (weekends
 * only when it includes them). Allocations are visited in order of their start, so the walk ends at the first one
 * that starts after the range.
 */
function computeHours(allocs, startDay, endDay) {
  if (endDay < startDay || allocs.dated.length === 0) return EMPTY_HOURS;

  const byProject = {};
  const byScope = {};
  const byGroup = {};
  const byTask = {};
  const maxTaskInScope = {};
  const maxTaskInGroup = {};
  let total = 0;

  for (const allocation of allocs.dated) {
    if (allocation.startDay > endDay) break;
    if (allocation.endDay < startDay) continue;

    const days = countAllocationDays(Math.max(allocation.startDay, startDay), Math.min(allocation.endDay, endDay), allocation.includeWeekends);
    const hours = days * allocation.hoursPerDay;

    total += hours;
    byTask[allocation.taskId] = hours;
    byProject[allocation.projectId] = (byProject[allocation.projectId] || 0) + hours;
    byScope[allocation.scopeId] = (byScope[allocation.scopeId] || 0) + hours;
    maxTaskInScope[allocation.scopeId] = Math.max(maxTaskInScope[allocation.scopeId] || 0, hours);
    if (allocation.groupId) {
      byGroup[allocation.groupId] = (byGroup[allocation.groupId] || 0) + hours;
      maxTaskInGroup[allocation.groupId] = Math.max(maxTaskInGroup[allocation.groupId] || 0, hours);
    }
  }

  return { total, byProject, byScope, byGroup, byTask, maxTaskInScope, maxTaskInGroup };
}

// The hours of one resource and period are asked for by every row of the resource, so they are worked out once per
// version of the resource's allocations and period. The list of dated allocations is replaced whenever an allocation
// changes and kept when only the covered tiles do, which makes it the key.
const cache = new WeakMap();

export function resourceHours(allocs, startDay, endDay) {
  let periods = cache.get(allocs.dated);
  if (!periods) {
    periods = new Map();
    cache.set(allocs.dated, periods);
  }

  const key = `${startDay}:${endDay}`;
  if (!periods.has(key)) {
    // Only the few periods a user looks at are kept.
    if (periods.size >= 8) periods.delete(periods.keys().next().value);
    periods.set(key, computeHours(allocs, startDay, endDay));
  }
  return periods.get(key);
}

/**
 * Hours a person can work in a closed range of days at a weekly rate: a fifth of it for every weekday.
 * Without a rate, a standard 40-hour week is assumed.
 */
export function periodCapacity(weeklyHours, startDay, endDay) {
  return round2(countWeekdays(startDay, endDay) * ((weeklyHours || 40) / 5));
}

/**
 * How a resource's weekly hours compare with the shares it has on its projects: a person whose project shares
 * add up to more than their own week is over-assigned.
 */
export function resourceAssignment(resource) {
  const resourceWeekly = resource.maxWeeklyHours ?? 40;
  const totalAssignmentWeekly = resource.memberships.reduce((sum, membership) => sum + (membership.maxWeeklyHours ?? 0), 0);

  return {
    resourceWeekly,
    totalAssignmentWeekly,
    isOverAssigned: totalAssignmentWeekly > resourceWeekly,
    overrun: Math.max(0, round2(totalAssignmentWeekly - resourceWeekly)),
  };
}
